import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { AvailabilityMap, ItemListOption } from '@/contexts/item-context-types';
import {
  ItemWithSubstitute,
  OrderItemWithSubstitutionType,
  OriginalVariantPack,
  SubstitutionItemWrapper,
} from './substitution-item-wrapper';
import { ListItemSeparator } from '@/components/list-item';
import {
  OrderSummaryItemPromotions,
  OrderSummaryListItem,
} from './order-summary-list-item';
import { StyleSheet } from 'react-native';
import { SubstitutionAction } from '@/enums';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useItemVisibleQuantity } from '@/stores/new-order-quantity-store';
import { useUserSettingsContext } from '@/contexts/user-context';

export type OrderSummaryListItemType =
  OrderItemWithSubstitutionType<ItemListOption>;

interface Props {
  availableQuantityMap: AvailabilityMap;
  isFocused: boolean;
  itemPromotionsMap?: { [orderItemUUID: string]: OrderSummaryItemPromotions };
  listItem: OrderSummaryListItemType;
  loyaltyPointsMap?: { [orderItemUUID: string]: number };
  onChooseSubstitutionPress: (
    itemWithSubstitute: ItemWithSubstitute<ItemListOption>,
  ) => Promise<void>;
  onClearSubstitutionPress: (
    originalVariantPack: OriginalVariantPack,
    substituteVariantPackUUID?: string,
  ) => Promise<void>;
  onDoNotSubstitutePress: (
    originalVariantPack: OriginalVariantPack,
  ) => Promise<void>;
  onEditNotes: () => void;
  onSalesRepPriceUpdated: () => void;
  orderUUID: string;
  quantityUpdatesPending: boolean;
  removeItems: (variantPackUUIDs: string[]) => Promise<void>;
  resetDisabled: boolean;
  updateQuantity: UpdateQuantityFn;
}

export const OrderSummaryMainContent: React.FC<Props> = ({
  availableQuantityMap,
  isFocused,
  itemPromotionsMap,
  loyaltyPointsMap,
  listItem,
  onChooseSubstitutionPress,
  onClearSubstitutionPress,
  onDoNotSubstitutePress,
  onEditNotes,
  onSalesRepPriceUpdated,
  orderUUID,
  quantityUpdatesPending,
  removeItems,
  resetDisabled,
  updateQuantity,
}) => {
  const { isDescriptionVisible } = useUserSettingsContext();
  const requireSubstitutionAction = useDynamicConfigValue(
    'require_substitution_action',
  );
  const substitutionVisibleQuantity = useItemVisibleQuantity(
    listItem.metadata?.substitutionVariantPack?.uuid,
  );

  const [isLoadingSubOptions, setIsLoadingSubOptions] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [isDoNotSubLoading, setIsDoNotSubLoading] = useState(false);

  const topLevelMetadata = listItem.metadata;
  if (!topLevelMetadata) {
    return null;
  }
  const substitutionVP = topLevelMetadata.substitutionVariantPack;
  const substitutionAction = topLevelMetadata.substitutionAction;
  const renderedSubAction = isDoNotSubLoading
    ? SubstitutionAction.DO_NOT_REPLACE
    : substitutionAction;

  const numItems = topLevelMetadata.orderItems.length;

  const resetSubstitutionItem = async (substituteVariantPackUUID?: string) => {
    if (!substitutionVP) {
      return;
    }
    setIsResetLoading(true);
    await onClearSubstitutionPress(substitutionVP, substituteVariantPackUUID);
    setIsResetLoading(false);
  };

  const content = topLevelMetadata.orderItems.map((oi, idx) => {
    const metadata = oi.item.metadata!;
    const isReplacementItem = Boolean(
      substitutionVP && metadata.variantPackUUID !== substitutionVP.uuid,
    );
    return (
      <React.Fragment key={oi.item.value}>
        <OrderSummaryListItem
          availableQuantityMap={availableQuantityMap}
          isDescriptionVisible={isDescriptionVisible}
          isFocused={isFocused}
          isReplacementItem={isReplacementItem}
          key={oi.item.value}
          loyaltyPoints={loyaltyPointsMap?.[oi.item.value]}
          onEditNotes={onEditNotes}
          onSalesRepPriceUpdated={onSalesRepPriceUpdated}
          option={oi.item}
          promotions={itemPromotionsMap?.[oi.item.value]}
          removeItems={removeItems}
          resetSubstitutionItem={() =>
            resetSubstitutionItem(oi.item.metadata!.variantPackUUID)
          }
          updateQuantity={updateQuantity}
        />
        {idx !== numItems - 1 ? (
          <ListItemSeparator style={styles.listItemSeparatorInner} />
        ) : null}
      </React.Fragment>
    );
  });

  const substituteVariantPackUUIDs = topLevelMetadata.orderItems.map(
    (oi) => oi.item.metadata!.variantPackUUID,
  );

  const visibleQuantityZero = Boolean(
    substitutionVP && substitutionVisibleQuantity === 0,
  );

  return substitutionVP ? (
    <SubstitutionItemWrapper
      action={renderedSubAction}
      actionButtons={[
        ...(requireSubstitutionAction
          ? []
          : [
              {
                children: i18n.t('do_not_substitute'),
                disabled: visibleQuantityZero || quantityUpdatesPending,
                loading: isDoNotSubLoading,
                onPress: async () => {
                  setIsDoNotSubLoading(true);
                  await onDoNotSubstitutePress(substitutionVP);
                  setIsDoNotSubLoading(false);
                },
                trackingData: {
                  name: 'item_substitution.do_not_substitute_button',
                  properties: {
                    orderUUID,
                    originalVariantPackUUID: substitutionVP.uuid,
                  },
                },
              },
            ]),
        {
          children: i18n.t('choose_substitute'),
          disabled: visibleQuantityZero || quantityUpdatesPending,
          loading: isLoadingSubOptions,
          onPress: async () => {
            const firstOrderItem = topLevelMetadata.orderItems[0];
            if (!firstOrderItem) {
              return;
            }
            setIsLoadingSubOptions(true);
            await onChooseSubstitutionPress(firstOrderItem);
            setIsLoadingSubOptions(false);
          },
          trackingData: {
            name: 'item_substitution.choose_substitute_button',
            properties: {
              orderUUID,
              originalVariantPackUUID: substitutionVP.uuid,
            },
          },
        },
      ]}
      noActionText={
        requireSubstitutionAction
          ? i18n.t('substitution_recommended')
          : i18n.t('low_inventory')
      }
      originalVariantPack={substitutionVP}
      resetButtonProps={{
        disabled: resetDisabled || isDoNotSubLoading || quantityUpdatesPending,
        loading: isResetLoading,
        onPress: resetSubstitutionItem,
        trackingData: {
          name: 'item_substitution.reset_button',
          properties: {
            action: substitutionAction,
            orderUUID,
            originalVariantPackUUID: substitutionVP.uuid,
            substituteVariantPackUUIDs,
          },
        },
      }}
    >
      {content}
    </SubstitutionItemWrapper>
  ) : (
    <>{content}</>
  );
};

const styles = StyleSheet.create({
  listItemSeparatorInner: {
    marginHorizontal: 8,
  },
});
